import { api } from '../../api'
import type {
  CustomEntity,
  CustomEntityListResponse,
  CustomSeizure,
  CustomSeizureListResponse,
  CustomProduct,
  CustomProductListResponse,
  SeizureActivity,
  CustomEntitiesStats,
  CustomEntitiesStatsParams,
  CreateEntityPayload,
  UpdateEntityPayload,
  CreateSeizurePayload,
  UpdateSeizurePayload,
  CreateProductPayload,
  UpdateProductPayload,
} from '../../../types/customEntities.types'

// --- Entities ---

export interface EntityListParams {
  page: number
  page_size?: number
  search?: string
  type?: string
  ordering?: string
  // Filter to entities directly linked to a given Ocean object.
  item?: string
  seller?: string
  saw?: string
}

export const getEntities = async (params: EntityListParams): Promise<CustomEntityListResponse> => {
  const res = await api.get('/kraken/entities/', { params })
  return res.data
}

export const getEntity = async (id: string): Promise<CustomEntity> => {
  const res = await api.get(`/kraken/entities/${id}/`)
  return res.data
}

export const createEntity = async (data: CreateEntityPayload): Promise<CustomEntity> => {
  const res = await api.post('/kraken/entities/', data)
  return res.data
}

export const updateEntity = async (id: string, data: UpdateEntityPayload): Promise<CustomEntity> => {
  const res = await api.patch(`/kraken/entities/${id}/`, data)
  return res.data
}

export const deleteEntity = async (id: string): Promise<void> => {
  await api.delete(`/kraken/entities/${id}/`)
}

// --- Entity ↔ Ocean object links (items / sellers / sites) ---

export type EntityLinkKind = 'item' | 'seller' | 'saw'

export const linkObjectToEntity = async (entityId: string, kind: EntityLinkKind, objectId: string): Promise<void> => {
  await api.post(`/kraken/entities/${entityId}/link_${kind}/`, { [`${kind}_id`]: objectId })
}

export const unlinkObjectFromEntity = async (entityId: string, kind: EntityLinkKind, objectId: string): Promise<void> => {
  await api.post(`/kraken/entities/${entityId}/unlink_${kind}/`, { [`${kind}_id`]: objectId })
}

// --- Seizures ---

export interface SeizureListParams {
  page: number
  page_size?: number
  search?: string
  ordering?: string
}

export const getSeizures = async (params: SeizureListParams): Promise<CustomSeizureListResponse> => {
  const res = await api.get('/kraken/seizures/', { params })
  return res.data
}

export const getSeizure = async (id: string): Promise<CustomSeizure> => {
  const res = await api.get(`/kraken/seizures/${id}/`)
  return res.data
}

export const createSeizure = async (data: CreateSeizurePayload): Promise<CustomSeizure> => {
  const res = await api.post('/kraken/seizures/', data)
  return res.data
}

export const updateSeizure = async (id: string, data: UpdateSeizurePayload): Promise<CustomSeizure> => {
  const res = await api.patch(`/kraken/seizures/${id}/`, data)
  return res.data
}

export const deleteSeizure = async (id: string): Promise<void> => {
  await api.delete(`/kraken/seizures/${id}/`)
}

export const linkEntityToSeizure = async (seizureId: string, entityId: string): Promise<void> => {
  await api.post(`/kraken/seizures/${seizureId}/link_entity/`, { entity_id: entityId })
}

export const unlinkEntityFromSeizure = async (seizureId: string, entityId: string): Promise<void> => {
  await api.post(`/kraken/seizures/${seizureId}/unlink_entity/`, { entity_id: entityId })
}

export const linkProductToSeizure = async (seizureId: string, productId: string): Promise<void> => {
  await api.post(`/kraken/seizures/${seizureId}/link_product/`, { product_id: productId })
}

export const unlinkProductFromSeizure = async (seizureId: string, productId: string): Promise<void> => {
  await api.post(`/kraken/seizures/${seizureId}/unlink_product/`, { product_id: productId })
}

export const linkItemToSeizure = async (seizureId: string, itemId: string): Promise<void> => {
  await api.post(`/kraken/seizures/${seizureId}/link_item/`, { item_id: itemId })
}

export const unlinkItemFromSeizure = async (seizureId: string, itemId: string): Promise<void> => {
  await api.post(`/kraken/seizures/${seizureId}/unlink_item/`, { item_id: itemId })
}

export const linkSellerToSeizure = async (seizureId: string, sellerId: string): Promise<void> => {
  await api.post(`/kraken/seizures/${seizureId}/link_seller/`, { seller_id: sellerId })
}

export const unlinkSellerFromSeizure = async (seizureId: string, sellerId: string): Promise<void> => {
  await api.post(`/kraken/seizures/${seizureId}/unlink_seller/`, { seller_id: sellerId })
}

export const getSeizureActivity = async (seizureId: string): Promise<SeizureActivity[]> => {
  const res = await api.get(`/kraken/seizures/${seizureId}/activity/`)
  return res.data
}

// --- Products ---

export interface ProductListParams {
  page: number
  page_size?: number
  search?: string
  ordering?: string
}

export const getProducts = async (params: ProductListParams): Promise<CustomProductListResponse> => {
  const res = await api.get('/kraken/products/', { params })
  return res.data
}

export const getProduct = async (id: string): Promise<CustomProduct> => {
  const res = await api.get(`/kraken/products/${id}/`)
  return res.data
}

const buildProductFormData = (data: Record<string, unknown>, photoFile?: File) => {
  const formData = new FormData()
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    if (Array.isArray(value)) {
      value.forEach((v) => formData.append(key, String(v)))
    } else if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value))
    } else {
      formData.append(key, String(value))
    }
  })
  if (photoFile) formData.append('photo', photoFile)
  return formData
}

export const createProduct = async (data: CreateProductPayload, photoFile?: File): Promise<CustomProduct> => {
  if (photoFile) {
    const res = await api.post('/kraken/products/', buildProductFormData(data as Record<string, unknown>, photoFile))
    return res.data
  }
  const res = await api.post('/kraken/products/', data)
  return res.data
}

export const updateProduct = async (id: string, data: UpdateProductPayload, photoFile?: File): Promise<CustomProduct> => {
  if (photoFile) {
    const res = await api.patch(`/kraken/products/${id}/`, buildProductFormData(data as Record<string, unknown>, photoFile))
    return res.data
  }
  const res = await api.patch(`/kraken/products/${id}/`, data)
  return res.data
}

export const deleteProduct = async (id: string): Promise<void> => {
  await api.delete(`/kraken/products/${id}/`)
}

// --- Stats ---

export const getCustomEntitiesStats = async (params: CustomEntitiesStatsParams): Promise<CustomEntitiesStats> => {
  const res = await api.get<CustomEntitiesStats>('/kraken/stats/', { params })
  return res.data
}
